import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert } from '@mui/material';
import useTranslate from '../../../customHooks/useTranslate';

const activeStyle = { borderColor: '#2196f3' };
const acceptStyle = { borderColor: '#00e676' };

const getImageDimensions = file =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            resolve({ width: img.width, height: img.height });
            URL.revokeObjectURL(url);
        };

        img.onerror = err => {
            URL.revokeObjectURL(url);
            reject(err);
        };

        img.src = url;
    });

export default function ImageDropZone({
    maxSizeAllowed,
    maxSizeAllowedMB,
    minWidthAllowed,
    minHeightAllowed,
    uploadFiles,
    setErrorMessage,
    disabled = false,
    disabledMessage,
}) {
    const translate = useTranslate();

    const { getRootProps, getInputProps, isDragActive, isDragAccept } =
        useDropzone({
            accept: 'image/jpeg, image/jpg, image/png',
            noClick: false, // permitir click en toda la zona
            noKeyboard: true,
            onDrop: async droppedFiles => {
                if (droppedFiles.length) {
                    const allowedBySize = droppedFiles.filter(
                        file => file.size < maxSizeAllowed
                    );

                    const sizeErrors = droppedFiles
                        .filter(file => file.size >= maxSizeAllowed)
                        .map(
                            file =>
                                `[${file.name}] => ${translate(
                                    'error_max_size_allowed'
                                )} ${maxSizeAllowedMB}MB`
                        );

                    const validFiles = [];
                    const dimensionErrors = [];

                    for (const file of allowedBySize) {
                        try {
                            const { width, height } = await getImageDimensions(
                                file
                            );

                            const invalidWidth =
                                minWidthAllowed && width < minWidthAllowed;
                            const invalidHeight =
                                minHeightAllowed && height < minHeightAllowed;

                            if (invalidWidth || invalidHeight) {
                                dimensionErrors.push(
                                    `[${file.name}] => ${translate(
                                        'error_min_dimensions'
                                    )} ${minWidthAllowed}x${minHeightAllowed}px (${width}x${height}px)`
                                );
                            } else {
                                validFiles.push(file);
                            }
                        } catch (e) {
                            dimensionErrors.push(
                                `[${file.name}] => ${translate(
                                    'error_invalid_image'
                                )}`
                            );
                        }
                    }

                    // 3) Mostrar errores y subir válidos
                    const allErrors = [...sizeErrors, ...dimensionErrors];
                    setErrorMessage(allErrors.join(', '));

                    if (validFiles.length) uploadFiles(validFiles);
                }
            },
        });

    const style = useMemo(
        () => ({
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
        }),
        [isDragActive, isDragAccept]
    );

    return (
        <div
            {...(!disabled ? getRootProps() : {})}
            className={`properties__image-dropzone ${
                disabled ? '--disabled' : ''
            }`}
            style={style}
        >
            <input {...getInputProps()} />
            {disabled ? (
                <Alert
                    severity='warning'
                    className='width100perc height100perc flex flex-center flex-align-center cursor-no-drop'
                >
                    <p>{translate(disabledMessage)}</p>
                </Alert>
            ) : (
                <p>{translate('drag_pictures')}</p>
            )}
        </div>
    );
}
